/**
 * Stakeholder Edit Route
 *
 * Edit an existing stakeholder using StakeholderForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useStakeholder } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { StakeholderForm } from '../../components/stakeholders';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import { updateStakeholder, type UpdateStakeholderInput, ApiError } from '../../lib/apiClient';
import type { CreateStakeholderFormData } from '../../components/forms';
import './edit.css';

export const Route = createFileRoute('/stakeholders/$stakeholderId/edit')({
  component: StakeholderEditPage,
});

function StakeholderEditPage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const { stakeholderId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch stakeholder data
  const { data, loading, error: fetchError } = useStakeholder(stakeholderId);
  const stakeholder = data?.stakeholder;

  if (loading) {
    return (
      <div className="stakeholder-edit-page">
        <LoadingSpinner centered message="Loading stakeholder..." />
      </div>
    );
  }

  if (fetchError || !stakeholder) {
    return (
      <div className="stakeholder-edit-page">
        <EmptyState
          icon="error"
          title="Failed to load stakeholder"
          description={fetchError?.message || 'Stakeholder not found'}
          actionLabel="Back to Stakeholders"
          onAction={() => navigate({ to: '/stakeholders', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  const handleSubmit = async (formData: CreateStakeholderFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build update input
      const updates: UpdateStakeholderInput = {
        name: formData.name,
        stakeholderType: formData.stakeholderType,
        description: formData.description || undefined,
        contactEmail: formData.contactEmail || undefined,
        organization: formData.organization || undefined,
      };

      await updateStakeholder(stakeholderId, updates);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['GetStakeholder', 'ListStakeholders'],
      });

      // Navigate back to stakeholder detail page
      navigate({ to: `/stakeholders/${stakeholderId}` as string });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: `/stakeholders/${stakeholderId}` as string });
  };

  // Map stakeholder data to form default values
  const defaultValues: Partial<CreateStakeholderFormData> = {
    name: stakeholder.name,
    stakeholderType: stakeholder.stakeholderType,
    description: stakeholder.description || undefined,
    contactEmail: stakeholder.contactEmail || undefined,
    organization: stakeholder.organization || undefined,
  };

  return (
    <div className="stakeholder-edit-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Stakeholder</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Update stakeholder information
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <StakeholderForm
        mode="edit"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
